/**
 * All Test Scenarios Page - Tree View with Folder Hierarchy
 * Features: Folder tree sidebar, List view with column selector, Folder CRUD
 * Independent module with test_scenario folder type
 */

import React, { useState, useEffect, useRef } from 'react';
import { apiClient, TestScenarioWithEvidence, Folder } from '../services/api';
import { FolderTree } from '../components/FolderTree';
import { ColumnSelector, ColumnConfig } from '../components/ColumnSelector';
import { ScenarioImport } from '../components/ScenarioImport';
import { ProjectFolderStats } from '../components/ProjectFolderStats';
import './AllScenariosPage.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

type Priority = 'Critical' | 'High' | 'Medium' | 'Low';
type Environment = 'Stage' | 'Preprod' | 'Prod';

const PREDEFINED_TAGS = [
  'Regression', 'Smoke', 'Sanity', 'Integration', 'E2E',
  'API', 'UI', 'Performance', 'Security', 'Functional'
];

interface ScenarioFormData {
  scenario_description: string;
  expected_result: string;
  priority: Priority;
  environment: Environment | '';
  tags: string[];
}

interface FolderFormData {
  name: string;
  description: string;
}

export const AllScenariosPage: React.FC = () => {
  // State
  const [folders, setFolders] = useState<Folder[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [scenarios, setScenarios] = useState<TestScenarioWithEvidence[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sc-sidebar-open');
    return saved === null ? false : saved !== 'true';
  });
  
  // Modals
  const [showScenarioForm, setShowScenarioForm] = useState(false);
  const [showFolderForm, setShowFolderForm] = useState(false);
  const [showRenameForm, setShowRenameForm] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedScenarios, setSelectedScenarios] = useState<Set<string>>(new Set());
  const [editingScenario, setEditingScenario] = useState<TestScenarioWithEvidence | null>(null);
  const [folderFormParentId, setFolderFormParentId] = useState<string | null>(null);
  const [renamingFolderId, setRenamingFolderId] = useState<string | null>(null);
  
  // Form data
  const [scenarioFormData, setScenarioFormData] = useState<ScenarioFormData>({
    scenario_description: '',
    expected_result: '',
    priority: 'Medium',
    environment: '',
    tags: []
  });
  
  const [folderFormData, setFolderFormData] = useState<FolderFormData>({
    name: '',
    description: ''
  });
  
  // Tags
  const [tagInput, setTagInput] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const tagInputRef = useRef<HTMLInputElement>(null);
  
  // Evidence files
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [evidenceFiles, setEvidenceFiles] = useState<File[]>([]);
  
  // Column configuration
  const [columns, setColumns] = useState<ColumnConfig[]>([
    { key: 'id', label: 'Scenario ID', visible: true, required: true },
    { key: 'description', label: 'Title', visible: true, required: true },
    { key: 'priority', label: 'Priority', visible: true },
    { key: 'tags', label: 'Tags', visible: true },
    { key: 'created_date', label: 'Created Date', visible: true },
    { key: 'created_by', label: 'Created By', visible: false },
    { key: 'actions', label: 'Actions', visible: true, required: true }
  ]);

  // Load folders on mount
  useEffect(() => {
    loadFolders();
  }, []);

  // Load scenarios when folder selected
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (selectedFolderId) {
      loadScenarios();
    } else {
      setScenarios([]);
    }
  }, [selectedFolderId]);

  // Load column preferences from localStorage
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const saved = localStorage.getItem('test-scenarios-columns');
    if (saved) {
      try {
        const visibilityMap = JSON.parse(saved);
        setColumns(cols => cols.map(col => ({
          ...col,
          visible: visibilityMap[col.key] ?? col.visible
        })));
      } catch (e) {
        console.error('Failed to load column preferences:', e);
      }
    }
  }, []);

  const loadFolders = async () => {
    try {
      const data = await apiClient.getFolders('test_scenario'); // Only load test_scenario folders
      setFolders(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load folders');
    }
  };

  const loadScenarios = async () => {
    if (!selectedFolderId) return;
    
    setIsLoading(true);
    setError(null);
    try {
      const data = await apiClient.getScenariosByFolder(selectedFolderId);
      setScenarios(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load scenarios');
    } finally {
      setIsLoading(false);
    }
  };

  const getBreadcrumb = (): Folder[] => {
    if (!selectedFolderId) return [];
    
    const path: Folder[] = [];
    // eslint-disable-next-line no-loop-func
    let current = folders.find(f => f.id === selectedFolderId);
    
    while (current) {
      path.unshift(current);
      // eslint-disable-next-line no-loop-func
      current = current.parent_id ? folders.find(f => f.id === current!.parent_id) : undefined;
    }
    
    return path;
  };

  // Scenario CRUD
  const handleCreateScenario = () => {
    if (!selectedFolderId) {
      setError('Please select a folder first');
      return;
    }
    
    setEditingScenario(null);
    setScenarioFormData({
      scenario_description: '',
      expected_result: '',
      priority: 'Medium',
      environment: '',
      tags: []
    });
    setTagInput('');
    setEvidenceFiles([]);
    setShowScenarioForm(true);
  };

  const handleEditScenario = (scenario: TestScenarioWithEvidence) => {
    setEditingScenario(scenario);
    setScenarioFormData({
      scenario_description: scenario.scenario_description,
      expected_result: scenario.expected_result || '',
      priority: scenario.priority,
      environment: scenario.environment || '',
      tags: scenario.tags || []
    });
    setTagInput('');
    setEvidenceFiles([]);
    setShowScenarioForm(true);
  };

  const handleSubmitScenario = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFolderId) return;

    setIsLoading(true);
    setError(null);

    try {
      const submitData = {
        ...scenarioFormData,
        expected_result: scenarioFormData.expected_result || undefined,
        environment: scenarioFormData.environment || undefined
      };

      if (editingScenario) {
        await apiClient.updateScenario(editingScenario.id, submitData);
        
        // Upload evidence if any (TODO: implement uploadEvidence API method)
        // if (evidenceFiles.length > 0) {
        //   await apiClient.uploadEvidence(editingScenario.id, evidenceFiles);
        // }
      } else {
        await apiClient.createScenario({
          folder_id: selectedFolderId,
          ...submitData
        });
        
        // Upload evidence if any (TODO: implement uploadEvidence API method)
        // if (evidenceFiles.length > 0 && created.id) {
        //   await apiClient.uploadEvidence(created.id, evidenceFiles);
        // }
      }
      
      setShowScenarioForm(false);
      setEditingScenario(null);
      setEvidenceFiles([]);
      await loadScenarios();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save scenario');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteScenario = async (scenarioId: string) => {
    if (!window.confirm('Are you sure you want to delete this scenario?')) {
      return;
    }

    try {
      await apiClient.deleteScenario(scenarioId);
      await loadScenarios();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete scenario');
    }
  };

  const handleBulkDeleteScenarios = async () => {
    if (selectedScenarios.size === 0) {
      setError('Please select at least one scenario to delete');
      return;
    }

    const count = selectedScenarios.size;
    if (!window.confirm(`Are you sure you want to delete ${count} selected scenario(s)? This cannot be undone.`)) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const scenarioIds = Array.from(selectedScenarios);
      const result = await apiClient.bulkDeleteScenarios(scenarioIds);
      
      if (result.failed_ids.length > 0) {
        setError(`Deleted ${result.deleted_count} of ${result.total_requested} scenarios. ${result.failed_ids.length} failed.`);
      }
      
      setSelectedScenarios(new Set()); // Clear selection
      await loadScenarios();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete scenarios');
    } finally {
      setIsLoading(false);
    }
  };

  // Folder CRUD
  const handleCreateSubfolder = (parentId: string) => {
    setFolderFormParentId(parentId);
    setFolderFormData({ name: '', description: '' });
    setShowFolderForm(true);
  };

  const handleSubmitFolder = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setIsLoading(true);
    setError(null);

    try {
      await apiClient.createFolder({
        name: folderFormData.name,
        description: folderFormData.description,
        parent_id: folderFormParentId || undefined,
        folder_type: 'test_scenario' // Explicitly set folder type
      });
      
      setShowFolderForm(false);
      setFolderFormParentId(null);
      await loadFolders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create folder');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRenameFolder = (folderId: string) => {
    const folder = folders.find(f => f.id === folderId);
    if (folder) {
      setRenamingFolderId(folderId);
      setFolderFormData({ name: folder.name, description: folder.description || '' });
      setShowRenameForm(true);
    }
  };

  const handleSubmitRename = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!renamingFolderId) return;
    
    setIsLoading(true);
    setError(null);

    try {
      await apiClient.updateFolder(renamingFolderId, {
        name: folderFormData.name,
        description: folderFormData.description
      });
      
      setShowRenameForm(false);
      setRenamingFolderId(null);
      await loadFolders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rename folder');
    } finally {
      setIsLoading(false);
    }
  };

  const handleImportExcel = () => {
    if (!selectedFolderId) {
      setError('Please select a folder first');
      return;
    }
    setShowImportModal(true);
  };

  const handleImportComplete = async () => {
    setShowImportModal(false);
    await loadScenarios();
  };

  const handleExportScenarios = async () => {
    if (!selectedFolderId) {
      setError('Please select a folder first');
      return;
    }
    
    if (selectedScenarios.size === 0) {
      setError('Please select at least one scenario to export');
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Get token from auth_tokens (same as api.ts)
      const authTokens = localStorage.getItem('auth_tokens');
      if (!authTokens) {
        throw new Error('Not authenticated. Please log in again.');
      }
      
      const tokens = JSON.parse(authTokens);
      const accessToken = tokens.access_token;
      
      // Use POST endpoint with proper structure
      const response = await fetch(`${API_BASE_URL}/api/v1/export/scenarios`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          format: 'excel',
          scope: 'scenarios',
          folder_id: selectedFolderId
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Export failed: ${response.status} - ${errorText}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `scenarios_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setSelectedScenarios(new Set()); // Clear selection after export
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export scenarios');
    } finally {
      setIsLoading(false);
    }
  };

  const handleShareLink = async () => {
    if (!selectedFolderId) {
      setError('Please select a folder first');
      return;
    }
    try {
      const folder = folders.find(f => f.id === selectedFolderId);
      if (folder?.link_token) {
        const link = `${window.location.origin}/shared/${folder.link_token}`;
        setShareLink(link);
        setShowShareModal(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get share link');
    }
  };

  const handleCreateTestRun = () => {
    if (!selectedFolderId) {
      setError('Please select a folder first');
      return;
    }
    window.location.href = `/test-runs/create?folder_id=${selectedFolderId}&type=scenario`;
  };

  const handleToggleScenario = (scenarioId: string) => {
    const newSelected = new Set(selectedScenarios);
    if (newSelected.has(scenarioId)) {
      newSelected.delete(scenarioId);
    } else {
      newSelected.add(scenarioId);
    }
    setSelectedScenarios(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedScenarios.size === filteredScenarios.length) {
      setSelectedScenarios(new Set());
    } else {
      setSelectedScenarios(new Set(filteredScenarios.map(s => s.id)));
    }
  };

  const handleDeleteFolder = async (folderId: string) => {
    const folder = folders.find(f => f.id === folderId);
    if (!folder) return;
    
    const hasChildren = folders.some(f => f.parent_id === folderId);
    const message = hasChildren
      ? `Delete "${folder.name}" and all its subfolders and scenarios? This cannot be undone.`
      : `Delete "${folder.name}" and all its scenarios? This cannot be undone.`;
    
    if (!window.confirm(message)) {
      return;
    }

    try {
      await apiClient.deleteFolder(folderId);
      if (selectedFolderId === folderId) {
        setSelectedFolderId(null);
      }
      await loadFolders();
    } catch (err: any) {
      // Handle specific error for folders with content
      const errorMessage = err?.response?.data?.detail || err?.message || 'Failed to delete folder';
      setError(errorMessage);
    }
  };

  // Tags
  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !scenarioFormData.tags.includes(tag)) {
      setScenarioFormData({ ...scenarioFormData, tags: [...scenarioFormData.tags, tag] });
      setTagInput('');
      setShowTagSuggestions(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleRemoveTag = (tagToRemove: string) => {
    setScenarioFormData({
      ...scenarioFormData,
      tags: scenarioFormData.tags.filter(t => t !== tagToRemove)
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSelectSuggestion = (tag: string) => {
    if (!scenarioFormData.tags.includes(tag)) {
      setScenarioFormData({ ...scenarioFormData, tags: [...scenarioFormData.tags, tag] });
    }
    setTagInput('');
    setShowTagSuggestions(false);
    tagInputRef.current?.focus();
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const filteredSuggestions = PREDEFINED_TAGS.filter(
    tag => tag.toLowerCase().includes(tagInput.toLowerCase()) && !scenarioFormData.tags.includes(tag)
  );

  // Evidence files
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setEvidenceFiles(Array.from(e.target.files));
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleRemoveFile = (index: number) => {
    setEvidenceFiles(files => files.filter((_, i) => i !== index));
  };

  // Filtering
  const filteredScenarios = searchTerm
    ? scenarios.filter(sc =>
        sc.scenario_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sc.scenario_description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (sc.expected_result && sc.expected_result.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (sc.tags && sc.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())))
      )
    : scenarios;

  const getEnvironmentColor = (env: string): string => {
    switch (env) {
      case 'Stage': return '#17a2b8';
      case 'Preprod': return '#ffc107';
      case 'Prod': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const breadcrumb = getBreadcrumb();

  return (
    <div className="test-scenarios-page-tree">
      {error && (
        <div className="error-banner">
          {error}
          <button onClick={() => setError(null)} className="error-close">×</button>
        </div>
      )}

      <div className="test-scenarios-layout">
        {/* Left Sidebar - Folder Tree */}
        <div className={`test-scenarios-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <div className="sidebar-inner">
            <FolderTree
              folders={folders}
              selectedFolderId={selectedFolderId}
              onSelectFolder={setSelectedFolderId}
              onCreateSubfolder={handleCreateSubfolder}
              onRenameFolder={handleRenameFolder}
              onDeleteFolder={handleDeleteFolder}
            />
          </div>
          <button
            className="sidebar-toggle-btn"
            onClick={() => setSidebarCollapsed(prev => {
              localStorage.setItem('sc-sidebar-open', String(prev));
              return !prev;
            })}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? '›' : '‹'}
          </button>
        </div>

        {/* Right Content Area */}
        <div className="test-scenarios-content">
          {selectedFolderId ? (
            <>
              {/* Breadcrumb */}
              <div className="content-breadcrumb">
                <span className="breadcrumb-icon">📍</span>
                {breadcrumb.map((folder, index) => (
                  <React.Fragment key={folder.id}>
                    {index > 0 && <span className="breadcrumb-separator">›</span>}
                    <span className="breadcrumb-item">{folder.name}</span>
                  </React.Fragment>
                ))}
              </div>

              {/* Action Bar */}
              <div className="content-actions">
                <button className="btn btn-secondary" onClick={() => handleCreateSubfolder(selectedFolderId)}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/><line x1="12" y1="11" x2="12" y2="17"/><line x1="9" y1="14" x2="15" y2="14"/></svg>
                  Create Subfolder
                </button>
                {(() => {
                  const selectedFolder = folders.find(f => f.id === selectedFolderId);
                  const isProjectFolder = selectedFolder?.level === 0;
                  return !isProjectFolder && (
                    <button className="btn btn-primary" onClick={handleCreateScenario}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                      Create Scenario
                    </button>
                  );
                })()}
              </div>
              {(() => {
                const selectedFolder = folders.find(f => f.id === selectedFolderId);
                const isProjectFolder = selectedFolder?.level === 0;
                return !isProjectFolder && (
                  <div className="action-bar">
                    <button className="btn btn-action" onClick={handleImportExcel} title="Import scenarios from Excel">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                      Import Excel
                    </button>
                    <button
                      className="btn btn-action"
                      onClick={handleExportScenarios}
                      disabled={selectedScenarios.size === 0}
                      title={selectedScenarios.size === 0 ? "Select scenarios to export" : `Export ${selectedScenarios.size} selected scenario(s)`}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                      Export ({selectedScenarios.size})
                    </button>
                    <button
                      className="btn btn-action btn-delete"
                      onClick={handleBulkDeleteScenarios}
                      disabled={selectedScenarios.size === 0}
                      title={selectedScenarios.size === 0 ? "Select scenarios to delete" : `Delete ${selectedScenarios.size} selected scenario(s)`}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                      Delete Selected ({selectedScenarios.size})
                    </button>
                    <div className="action-bar-divider" />
                    <button className="btn btn-action" onClick={handleShareLink} title="Get shareable link for this folder">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                      Share Link
                    </button>
                    <button className="btn btn-action" onClick={handleCreateTestRun} title="Create test run from scenarios">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/></svg>
                      Create Test Run
                    </button>
                  </div>
                );
              })()}

              {/* Show stats for project folders (level 0), otherwise show controls */}
              {(() => {
                const selectedFolder = folders.find(f => f.id === selectedFolderId);
                const isProjectFolder = selectedFolder?.level === 0;
                
                if (isProjectFolder) {
                  return <ProjectFolderStats folderId={selectedFolderId} folderType="test_scenario" />;
                }
                
                return (
                  <>
                    {/* Controls Bar */}
                    <div className="content-controls">
                      <input
                        type="text"
                        className="search-input"
                        placeholder="Search scenarios..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      
                      <ColumnSelector
                        columns={columns}
                        onColumnsChange={setColumns}
                        storageKey="test-scenarios-columns"
                      />
                      
                      <div className="stats-badge">
                        Total: <strong>{scenarios.length}</strong>
                      </div>
                      {searchTerm && (
                        <div className="stats-badge filtered">
                          Filtered: <strong>{filteredScenarios.length}</strong>
                        </div>
                      )}
                    </div>

                    {/* Scenarios List */}
                    {isLoading ? (
                      <div className="content-loading">Loading scenarios...</div>
                    ) : filteredScenarios.length === 0 ? (
                      <div className="content-empty">
                        <p>{searchTerm ? 'No scenarios match your search' : 'No scenarios in this folder'}</p>
                        <button className="btn btn-primary" onClick={handleCreateScenario}>
                          Create First Scenario
                        </button>
                      </div>
                    ) : (
                      <div className="test-scenarios-table-container">
                        <div className="test-scenarios-table-wrapper">
                        <table className="test-scenarios-table">
                          <thead>
                            <tr>
                              <th className="col-checkbox">
                                <input
                                  type="checkbox"
                                  checked={selectedScenarios.size === filteredScenarios.length && filteredScenarios.length > 0}
                                  onChange={handleSelectAll}
                                  title="Select all"
                                />
                              </th>
                              {columns.filter(col => col.visible).map(col => (
                                <th key={col.key} className={`col-${col.key}`}>
                                  {col.label}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {filteredScenarios.map(scenario => (
                              <tr key={scenario.id} className={selectedScenarios.has(scenario.id) ? 'selected-row' : ''}>
                                <td className="col-checkbox">
                                  <input
                                    type="checkbox"
                                    checked={selectedScenarios.has(scenario.id)}
                                    onChange={() => handleToggleScenario(scenario.id)}
                                  />
                                </td>
                                {columns.find(c => c.key === 'id')?.visible && (
                                  <td className="col-id">
                                    <span className="scenario-id">{scenario.scenario_id}</span>
                                  </td>
                                )}
                          {columns.find(c => c.key === 'description')?.visible && (
                            <td className="col-description">
                              <div className="description-cell">
                                <strong>{scenario.scenario_description}</strong>
                              </div>
                            </td>
                          )}
                          {columns.find(c => c.key === 'environment')?.visible && (
                            <td className="col-environment">
                              {scenario.environment ? (
                                <span
                                  className="environment-badge"
                                  style={{ backgroundColor: getEnvironmentColor(scenario.environment) }}
                                >
                                  {scenario.environment}
                                </span>
                              ) : (
                                <span className="no-data">—</span>
                              )}
                            </td>
                          )}
                          {columns.find(c => c.key === 'priority')?.visible && (
                            <td className="col-priority">
                              <span className={`priority-badge priority-${scenario.priority.toLowerCase()}`}>
                                {scenario.priority}
                              </span>
                            </td>
                          )}
                          {columns.find(c => c.key === 'tags')?.visible && (
                            <td className="col-tags">
                              {scenario.tags && scenario.tags.length > 0 ? (
                                <div className="tags-cell">
                                  {scenario.tags.map(tag => (
                                    <span key={tag} className="tag-badge">{tag}</span>
                                  ))}
                                </div>
                              ) : (
                                <span className="no-data">—</span>
                              )}
                            </td>
                          )}
                          {columns.find(c => c.key === 'expected')?.visible && (
                            <td className="col-expected">
                              {scenario.expected_result || <span className="no-data">—</span>}
                            </td>
                          )}
                          {columns.find(c => c.key === 'evidence')?.visible && (
                            <td className="col-evidence">
                              {scenario.expected_evidence && scenario.expected_evidence.length > 0 ? (
                                <span className="evidence-count">{scenario.expected_evidence.length} files</span>
                              ) : (
                                <span className="no-data">—</span>
                              )}
                            </td>
                          )}
                          {columns.find(c => c.key === 'created_date')?.visible && (
                            <td className="col-created-date">
                              {new Date(scenario.created_at).toLocaleDateString()}
                            </td>
                          )}
                          {columns.find(c => c.key === 'created_by')?.visible && (
                            <td className="col-created-by">
                              {scenario.created_by}
                            </td>
                          )}
                          {columns.find(c => c.key === 'actions')?.visible && (
                            <td className="col-actions">
                              <div className="action-buttons">
                                <button
                                  className="action-btn edit"
                                  onClick={() => handleEditScenario(scenario)}
                                  title="Edit"
                                >
                                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                </button>
                                <button
                                  className="action-btn delete"
                                  onClick={() => handleDeleteScenario(scenario.id)}
                                  title="Delete"
                                >
                                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </div>
              )}
                  </>
                );
              })()}
            </>
          ) : (
            <div className="content-empty-state">
              <div className="empty-state-icon">📁</div>
              <h3>Select a Folder</h3>
              <p>Choose a folder from the tree to view and manage test scenarios</p>
            </div>
          )}
        </div>
      </div>

      {/* Scenario Form Modal */}
      {showScenarioForm && (
        <div className="sc-modal-overlay" onClick={() => setShowScenarioForm(false)}>
          <div className="sc-modal" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="sc-modal-header">
              <div>
                <h2 className="sc-modal-title">{editingScenario ? 'Edit Test Scenario' : 'Create New Test Scenario'}</h2>
                <p className="sc-modal-subtitle">
                  {breadcrumb.map(f => f.name).join(' • ').toUpperCase() || 'TEST SCENARIO'}
                </p>
              </div>
              <button className="sc-modal-close" onClick={() => setShowScenarioForm(false)} title="Close">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            <form onSubmit={handleSubmitScenario} className="sc-modal-body">
              {/* Title */}
              <div className="sc-field">
                <label className="sc-label">Scenario Title</label>
                <input
                  type="text"
                  className="sc-input"
                  value={scenarioFormData.scenario_description}
                  onChange={(e) => setScenarioFormData({ ...scenarioFormData, scenario_description: e.target.value })}
                  placeholder="Enter scenario title"
                  required
                  autoFocus
                />
              </div>

              {/* Priority Cards */}
              <div className="sc-field">
                <label className="sc-label">Priority Level</label>
                <div className="sc-priority-cards">
                  {(['High', 'Medium', 'Low'] as Priority[]).map(p => (
                    <button
                      key={p}
                      type="button"
                      className={`sc-priority-card sc-priority-${p.toLowerCase()} ${scenarioFormData.priority === p ? 'sc-priority-card-active' : ''}`}
                      onClick={() => setScenarioFormData({ ...scenarioFormData, priority: p })}
                    >
                      <span className="sc-priority-icon">
                        {p === 'High' && (
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <polyline points="17 11 12 6 7 11"/>
                            <polyline points="17 18 12 13 7 18"/>
                          </svg>
                        )}
                        {p === 'Medium' && (
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <line x1="5" y1="9" x2="19" y2="9"/>
                            <line x1="5" y1="15" x2="19" y2="15"/>
                          </svg>
                        )}
                        {p === 'Low' && (
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <line x1="5" y1="8" x2="19" y2="8"/>
                            <line x1="5" y1="14" x2="19" y2="14"/>
                            <line x1="5" y1="20" x2="19" y2="20"/>
                          </svg>
                        )}
                      </span>
                      <span className="sc-priority-label">{p}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Tags */}
              <div className="sc-field">
                <div className="sc-tags-header">
                  <label className="sc-label">Classification Tags</label>
                  <span className="sc-tags-hint">SELECT MULTIPLE</span>
                </div>
                <div className="sc-tags-grid">
                  {PREDEFINED_TAGS.map(tag => (
                    <button
                      key={tag}
                      type="button"
                      className={`sc-tag-pill ${scenarioFormData.tags.includes(tag) ? 'sc-tag-pill-active' : ''}`}
                      onClick={() => {
                        if (scenarioFormData.tags.includes(tag)) {
                          setScenarioFormData({ ...scenarioFormData, tags: scenarioFormData.tags.filter(t => t !== tag) });
                        } else {
                          setScenarioFormData({ ...scenarioFormData, tags: [...scenarioFormData.tags, tag] });
                        }
                      }}
                    >
                      {scenarioFormData.tags.includes(tag) && (
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                      )}
                      {tag}
                    </button>
                  ))}
                </div>
              </div>

              {/* Footer */}
              <div className="sc-modal-footer">
                <button type="button" className="sc-btn-cancel" onClick={() => setShowScenarioForm(false)}>
                  Cancel and Discard
                </button>
                <button type="submit" className="sc-btn-submit" disabled={isLoading}>
                  {isLoading
                    ? (editingScenario ? 'Saving...' : 'Creating...')
                    : (editingScenario ? 'Save Changes' : 'Create Scenario')}
                  {!isLoading && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Folder Modal */}
      {showFolderForm && (
        <div className="modal-overlay" onClick={() => setShowFolderForm(false)}>
          <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create Subfolder</h3>
              <button onClick={() => setShowFolderForm(false)} className="modal-close">×</button>
            </div>
            
            <form onSubmit={handleSubmitFolder} className="modal-form">
              <div className="form-group">
                <label>Folder Name <span className="required">*</span></label>
                <input
                  type="text"
                  className="form-control"
                  value={folderFormData.name}
                  onChange={(e) => setFolderFormData({ ...folderFormData, name: e.target.value })}
                  required
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  className="form-control"
                  value={folderFormData.description}
                  onChange={(e) => setFolderFormData({ ...folderFormData, description: e.target.value })}
                  rows={2}
                />
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                  {isLoading ? 'Creating...' : 'Create'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowFolderForm(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Rename Folder Modal */}
      {showRenameForm && (
        <div className="modal-overlay" onClick={() => setShowRenameForm(false)}>
          <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Rename Folder</h3>
              <button onClick={() => setShowRenameForm(false)} className="modal-close">×</button>
            </div>
            
            <form onSubmit={handleSubmitRename} className="modal-form">
              <div className="form-group">
                <label>Folder Name <span className="required">*</span></label>
                <input
                  type="text"
                  className="form-control"
                  value={folderFormData.name}
                  onChange={(e) => setFolderFormData({ ...folderFormData, name: e.target.value })}
                  required
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  className="form-control"
                  value={folderFormData.description}
                  onChange={(e) => setFolderFormData({ ...folderFormData, description: e.target.value })}
                  rows={2}
                />
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                  {isLoading ? 'Saving...' : 'Save'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowRenameForm(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Share Link Modal */}
      {showShareModal && (
        <div className="modal-overlay" onClick={() => setShowShareModal(false)}>
          <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Share Folder Link</h3>
              <button onClick={() => setShowShareModal(false)} className="modal-close">×</button>
            </div>
            <div className="modal-body">
              <p>Share this link to give others access to this folder:</p>
              <div className="share-link-container">
                <input 
                  type="text" 
                  className="form-control" 
                  value={shareLink} 
                  readOnly 
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                />
                <button 
                  className="btn btn-primary"
                  onClick={() => {
                    navigator.clipboard.writeText(shareLink);
                    alert('Link copied to clipboard!');
                  }}
                >
                  Copy
                </button>
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowShareModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && selectedFolderId && (
        <div className="modal-overlay" onClick={() => setShowImportModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Import Scenarios from Excel</h3>
              <button onClick={() => setShowImportModal(false)} className="modal-close">×</button>
            </div>
            <div className="modal-body">
              <ScenarioImport
                folderId={selectedFolderId}
                onImportComplete={handleImportComplete}
                onCancel={() => setShowImportModal(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
